import delay from '@redux-saga/delay-p';
import { all, call, put, takeEvery, CallEffect, PutEffect } from 'redux-saga/effects';
import { RequestActionType, requestError, requestSuccess } from 'commons/src/actions/requestActions';
import { pollingDelayMs } from 'commons/src/constants';
import { CurrentDashboardResponse } from 'commons/src/models/commonTypeScript';
import { createSensorGraphArrays } from 'commons/src/sagas/DashboardSagas/dashboardData';
import { toErrorType } from 'commons/src/sagas/isErrorType';
import {
    GetPublicDashboard,
    GetPublicDashboardSuccess,
    getPublicDashboardSuccess,
    GetPublicDashboardTiles,
    getPublicDashboardTiles,
    GetPublicDashboardTilesSuccess,
    getPublicDashboardTilesSuccess,
    PublicDashboardActionType,
} from '../actions/publicDashboardActions';
import { fetchPublicDashboardInfo, fetchPublicDashboardTiles } from '../api/publicDashboardApi';
import { PublicDashboardProps } from '../models/commons';
import RequestActions from '../models/RequestTypes';
import { PublicRequestType } from '../reducers/requestReducer';

type FetchPublicDashboardTilesSagaActionType = Generator<
    CallEffect<CurrentDashboardResponse> | CallEffect | PutEffect<GetPublicDashboardTilesSuccess> | RequestActions,
    void,
    CurrentDashboardResponse
>;
export function* fetchPublicDashboardTilesSaga({
    payload,
}: GetPublicDashboard): FetchPublicDashboardTilesSagaActionType {
    while (true) {
        try {
            // as long as there are no menu to take you from the public dashboard this while loop will stop on error or when leaving the page
            const dashboardTiles = yield call(fetchPublicDashboardTiles, payload);
            const tilesData = createSensorGraphArrays(dashboardTiles as CurrentDashboardResponse, {
                sensorData: {},
                minValues: {},
                averageValues: {},
            });
            yield put(getPublicDashboardTilesSuccess(tilesData));
            yield put(requestSuccess(PublicRequestType.FetchPublicDashboardTiles, RequestActionType.Success));
            yield call(delay, pollingDelayMs);
        } catch (error) {
            const errorAsErrorType = toErrorType(error);
            yield put(
                requestError(errorAsErrorType, PublicRequestType.FetchPublicDashboardTiles, RequestActionType.Error)
            );
            break;
        }
    }
}

type FetchPublicDashboardSagaActionType = Generator<
    | CallEffect<PublicDashboardProps>
    | PutEffect<GetPublicDashboardTiles>
    | PutEffect<GetPublicDashboardSuccess>
    | RequestActions,
    void,
    PublicDashboardProps
>;
export function* fetchPublicDashboardSaga({ payload }: GetPublicDashboard): FetchPublicDashboardSagaActionType {
    try {
        const dashboardInfo: PublicDashboardProps = yield call(fetchPublicDashboardInfo, payload);
        yield put(getPublicDashboardTiles(payload));

        yield put(
            getPublicDashboardSuccess({
                info: dashboardInfo,
            })
        );
        yield put(requestSuccess(PublicRequestType.FetchPublicDashboard, RequestActionType.Success));
    } catch (error) {
        const errorAsErrorType = toErrorType(error);
        yield put(requestError(errorAsErrorType, PublicRequestType.FetchPublicDashboard, RequestActionType.Error));
    }
}

export default function* (): Generator {
    yield all([
        takeEvery(PublicDashboardActionType.FetchPublicDashboard, fetchPublicDashboardSaga),
        takeEvery(PublicDashboardActionType.FetchPublicDashboardTiles, fetchPublicDashboardTilesSaga),
    ]);
}
